'use client'

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/getting-started/my-corner.mdx
 * (published at user-docs.totalaccess.co.za/getting-started/my-corner#switching-clients)
 */

import { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { Building2, X, ArrowLeftRight, Loader2, Check, Search } from 'lucide-react'
import { clientService, type ClientInfo } from '@/lib/clientService'
import { useNavigation } from '@/hooks/useNavigation'
import { isCapacitor, isTauri } from '@/lib/platformUtils'
import { isOperationsDemoOverrideEnabled, OPERATIONS_DEMO_SESSION_UPDATED_EVENT } from '@/lib/demo/clientContext'
import { OnboardWithClientModal } from './OnboardWithClientModal'
import ModalFooter from './shared/ModalFooter'
import UserDocsLink from './shared/UserDocsLink'
import '@/app/professional-modern.css'

const BOTTOM_SAFE_AREA = 'env(safe-area-inset-bottom, 0px)'
const CLIENT_SELECTOR_OVERLAY_Z_CLASS = 'z-[135]'

interface ClientSelectorProps {
  isOpen: boolean
  onClose: () => void
  onClientSwitch?: (client: ClientInfo) => void
  onOnboardNewClient?: () => void
  /**
   * Optional URL to navigate to after a successful client switch (web only).
   * Defaults to /client-zone/dashboard. Ignored on native app shells when
   * onClientSwitch is provided (the parent handles navigation).
   */
  returnUrl?: string
}

export default function ClientSelector({ isOpen, onClose, onClientSwitch, onOnboardNewClient, returnUrl }: ClientSelectorProps) {
  const [clients, setClients] = useState<ClientInfo[]>([])
  const [activeClient, setActiveClient] = useState<ClientInfo | null>(null)
  const [selectedClient, setSelectedClient] = useState<number | null>(null)
  const [setAsDefault, setSetAsDefault] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showOnboardModal, setShowOnboardModal] = useState(false)
  const [isDemoModeActive, setIsDemoModeActive] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const { refreshNavigation } = useNavigation()
  const isMobileApp = isCapacitor()
  const isDesktopApp = isTauri()

  // Clean malformed URLs (fix doubled protocols like httphttps://)
  const cleanImageUrl = (url: string | undefined): string | undefined => {
    if (!url) return undefined;
    // Fix doubled protocols
    return url.replace(/^https?https?:\/\//, 'https://').replace(/^httphttp:\/\//, 'http://');
  }

  // Handle image error for logos
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.style.display = 'none'
  }

  const loadClients = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const data = await clientService.getUserClients()

      if (data) {
        // Clients are now sorted by ID ascending from the API
        setClients(data.available_clients)
        setActiveClient(data.active_client)
        setSelectedClient(data.active_client.id)
      }
    } catch (err) {
      console.error('Error loading clients:', err)
      setError(err instanceof Error ? err.message : 'Failed to load clients')
    } finally {
      setIsLoading(false)
    }
  }

  // Load clients from API
  useEffect(() => {
    if (isOpen) {
      setIsDemoModeActive(isOperationsDemoOverrideEnabled())
      setSearchQuery('')
      loadClients()
    }
  }, [isOpen])

  const filteredClients = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return clients
    return clients.filter((c) => {
      const name = (c.name || '').toLowerCase()
      const trading = (c.trading_name || '').toLowerCase()
      const role = (c.role || '').toLowerCase()
      const idStr = String(c.id)
      return name.includes(q) || trading.includes(q) || role.includes(q) || idStr.includes(q)
    })
  }, [clients, searchQuery])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const syncDemoState = () => {
      setIsDemoModeActive(isOperationsDemoOverrideEnabled())
    }

    syncDemoState()

    window.addEventListener(OPERATIONS_DEMO_SESSION_UPDATED_EVENT, syncDemoState as EventListener)
    window.addEventListener('storage', syncDemoState)
    window.addEventListener('focus', syncDemoState)

    return () => {
      window.removeEventListener(OPERATIONS_DEMO_SESSION_UPDATED_EVENT, syncDemoState as EventListener)
      window.removeEventListener('storage', syncDemoState)
      window.removeEventListener('focus', syncDemoState)
    }
  }, [])


  const mobileSafeBottom = isMobileApp
    ? `calc(1rem + ${BOTTOM_SAFE_AREA})`
    : undefined

  // Listen for active client changes from heartbeat
  useEffect(() => {
    const handleActiveClientChanged = (event: CustomEvent) => {
      console.log('[CLIENT_SELECTOR] Active client changed from heartbeat:', event.detail)

      // Refresh navigation data
      refreshNavigation().then(() => {
        console.log('[CLIENT_SELECTOR] Navigation refreshed after heartbeat client change')
      }).catch((error) => {
        console.error('[CLIENT_SELECTOR] Error refreshing navigation after heartbeat:', error)
      })

      // Reload client data if modal is open
      if (isOpen) {
        loadClients().then(() => {
          console.log('[CLIENT_SELECTOR] Client data reloaded after heartbeat client change')
        }).catch((error) => {
          console.error('[CLIENT_SELECTOR] Error reloading clients after heartbeat:', error)
        })
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('activeClientChanged', handleActiveClientChanged as EventListener)

      return () => {
        window.removeEventListener('activeClientChanged', handleActiveClientChanged as EventListener)
      }
    }
  }, [isOpen, refreshNavigation])

  const handleClientSwitch = async () => {
    if (!selectedClient) return

    if (isOperationsDemoOverrideEnabled()) {
      setError('Exit Operations Demo before switching clients.')
      return
    }

    setIsSwitching(true)
    setError(null)

    try {
      const success = await clientService.switchClient(selectedClient, setAsDefault)

      if (success) {
        console.log('Successfully switched to client:', selectedClient)

        // Find the selected client from our list
        const switchedClient = clients.find(c => c.id === selectedClient)

        // Close modal first so the UI dismisses before navigation/refresh work.
        onClose()

        if (onClientSwitch && switchedClient) {
          void Promise.resolve(onClientSwitch(switchedClient)).catch((err) => {
            console.error('[CLIENT_SELECTOR] onClientSwitch failed:', err)
          })
        }

        if (isMobileApp || isDesktopApp) {
          // Native app shells (Capacitor + Tauri): the WebView caches state
          // aggressively. A full reload is required to clear all in-memory
          // client-scoped state (auth context, navigation data, badges, etc.)
          // after switching clients, even if the parent provided a handler.
          if (onClientSwitch && switchedClient) {
            // Let the parent handler run first (e.g. close modals), then reload.
            // The onClientSwitch promise is already awaited above — just reload.
            console.log('[CLIENT SWITCH] Native app shell — parent handler ran, now reloading');
          } else {
            console.log('[CLIENT SWITCH] App-shell fallback reload (no parent handler)');
          }
          window.location.reload();
        } else {
          // Notify service worker to clear cache and sync new client data
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            try {
              navigator.serviceWorker.controller.postMessage({
                type: 'CLIENT_SWITCH',
                clientId: selectedClient,
                action: 'clear_and_resync'
              });
              console.log('[CLIENT SWITCH] Notified service worker of client switch');
            } catch (e) {
              console.warn('[CLIENT SWITCH] Error notifying service worker:', e);
            }
          }

          // Web: full page reload for clean state reset
          console.log('[CLIENT SWITCH] Reloading page to refresh all client data...')
          window.location.href = returnUrl || '/client-zone/dashboard'
        }
      }
    } catch (err) {
      console.error('Error switching client:', err)
      setError(err instanceof Error ? err.message : 'Failed to switch client')
    } finally {
      setIsSwitching(false)
    }
  }

  if (!isOpen) return null

  const modalContent = (
    <>
      {/* Full-screen overlay */}
      <div
        className={`fixed inset-0 z-[135] bg-gray-50 dark:bg-gray-900 overflow-y-auto ${isMobileApp ? '' : ''
          }`}
        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={onClose}
      >
        <div className="flex min-h-full flex-col px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="mb-3 sm:mb-6 px-2">
            <div className="relative">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
                  <Building2 className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1">Select Client</h1>
              </div>
              <div className="absolute right-0 top-0 flex items-center gap-2">
                <UserDocsLink
                  docsPath="/getting-started/my-corner#switching-clients"
                  label="User guide: Switching clients"
                />
                <button
                  onClick={onClose}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <main className="grid grid-cols-1 gap-2 px-2 md:grid-cols-2 sm:gap-4 flex-1">
            {error && (
              <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
                <div className="pm-alert pm-alert-error">
                  {error}
                </div>
              </div>
            )}

            {isDemoModeActive && (
              <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
                <div className="pm-alert border border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                  Exit Operations Demo before switching clients.
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
                <div className="text-center py-10 flex flex-col justify-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-teal-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 mb-4 mx-auto">
                    <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Loading clients...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
                  {/* Search */}
                  <div className="relative">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                      aria-hidden
                    />
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name, trading name, or role…"
                      autoComplete="off"
                      className="w-full rounded-xl border-2 border-gray-200 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 dark:bg-gray-900 dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                      aria-label="Search clients"
                    />
                  </div>
                </div>

                {/* Filtered list */}
                <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
                  <div
                    className="max-h-100 overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-gray-50 p-2 space-y-1.5 dark:border-gray-700 dark:bg-gray-900/40"
                    role="listbox"
                    aria-label="Client list"
                  >
                    {filteredClients.length === 0 ? (
                      <p className="text-sm text-center text-gray-600 py-8 px-2 dark:text-gray-400">
                        {clients.length === 0
                          ? 'No clients available.'
                          : 'No clients match your search.'}
                      </p>
                    ) : (
                      filteredClients.map((client) => {
                        const isSelected = selectedClient === client.id
                        const isCurrent = activeClient && client.id === activeClient.id

                        return (
                          <div
                            key={client.id}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => client.is_active && setSelectedClient(client.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                if (client.is_active) setSelectedClient(client.id)
                              }
                            }}
                            tabIndex={client.is_active ? 0 : -1}
                            className={`group flex items-center p-2.5 sm:p-3 rounded-lg cursor-pointer transition-all duration-200 border-2 ${isSelected
                              ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-blue-50 dark:from-teal-900/20 dark:to-blue-900/20 shadow-sm'
                              : 'border-transparent hover:border-gray-300 hover:bg-white dark:hover:border-gray-600 dark:hover:bg-gray-800'
                              } ${!client.is_active ? 'opacity-40 !cursor-not-allowed' : ''}`}
                          >
                            <div className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 mr-3">
                              {client.logo_url ? (
                                <Image
                                  src={cleanImageUrl(client.logo_url) || ''}
                                  alt={client.name}
                                  width={44}
                                  height={44}
                                  className="w-full h-full object-contain rounded-lg border border-gray-200 dark:border-gray-700"
                                  onError={handleImageError}
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-lg flex items-center justify-center">
                                  <Building2 className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 text-sm truncate dark:text-white">{client.name}</h3>
                              {client.trading_name && client.trading_name !== client.name && (
                                <p className="text-xs text-gray-600 truncate dark:text-gray-400">{client.trading_name}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className="pm-badge pm-badge-blue text-[10px] sm:text-xs">{client.role}</span>
                                {isCurrent && (
                                  <span className="pm-badge pm-badge-teal text-[10px] sm:text-xs">Current</span>
                                )}
                              </div>
                            </div>

                            <div className="flex-shrink-0 ml-2">
                              <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected
                                  ? 'border-teal-500 bg-teal-500'
                                  : 'border-gray-300 group-hover:border-teal-300 dark:border-gray-600'
                                  }`}
                              >
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>

              </>
            )}
          </main>

          <ModalFooter
            className=""
            message={!selectedClient ? 'Select a client to switch' : isDemoModeActive ? 'Exit Operations Demo before switching clients' : undefined}
            start={(
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={setAsDefault}
                  onChange={(e) => setSetAsDefault(e.target.checked)}
                  className="w-4 h-4 text-teal-600 bg-white border-gray-300 rounded focus:ring-teal-500 dark:bg-gray-900 dark:border-gray-700 dark:focus:ring-teal-600 dark:ring-offset-gray-800 focus:ring-2"
                />
                <span className="ml-2.5 text-sm text-gray-600 group-hover:text-gray-900 transition-colors dark:text-gray-400 dark:group-hover:text-gray-300">
                  Set as my default client
                </span>
              </label>
            )}
          >
            {!(isMobileApp || isDesktopApp) && (
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleClientSwitch}
              disabled={!selectedClient || isSwitching || isDemoModeActive}
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-lg px-4 py-2 font-medium text-white transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)' }}
            >
              {isSwitching ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Switching...
                </>
              ) : (
                <>
                  <ArrowLeftRight className="w-4 h-4" />
                  Switch Client
                </>
              )}
            </button>
          </ModalFooter>
        </div>
      </div>

      {/* Onboard Modal */}
      <OnboardWithClientModal
        isOpen={showOnboardModal}
        onClose={() => setShowOnboardModal(false)}
        onSuccess={() => {
          setShowOnboardModal(false)
          loadClients() // Refresh client list
        }}
      />
    </>
  )

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent
}
